import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, signInWithCustomTokenAuth, hasRole, getUserData } from '../../../services/auth';
import { useAuth } from '../../../contexts/AuthContext';
import { api } from '../../../services/api';
import { Shield, ArrowRight } from 'lucide-react';

const AdminLogin = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminUid, setAdminUid] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminSignInLoading, setAdminSignInLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Check if current user is admin (but don't redirect - allow them to stay on admin login page)
  useEffect(() => {
    const checkAdmin = async () => {
      if (user && !authLoading) {
        try {
          const userData = await getUserData(user.uid);
          const adminStatus = hasRole(userData, 'admin');
          setIsAdmin(adminStatus);
          // Don't redirect - allow admin to stay on this page to use impersonation
        } catch (error) {
          console.error('Error checking admin status:', error);
          // If error checking admin, don't redirect - let them try again
        }
      }
    };
    checkAdmin();
  }, [user, authLoading]);

  const handleAdminAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const signedInUser = await signIn(adminEmail, adminPassword);
      
      // Wait a moment for auth state to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Check admin status after sign in - try multiple times if needed
      let userData = null;
      let attempts = 0;
      while (!userData && attempts < 3) {
        userData = await getUserData(signedInUser.uid);
        if (!userData) {
          await new Promise(resolve => setTimeout(resolve, 500));
          attempts++;
        }
      }
      
      if (!userData) {
        setError('Could not fetch user data. Please try again.');
        setLoading(false);
        return;
      }
      
      const adminStatus = hasRole(userData, 'admin');
      setIsAdmin(adminStatus);
      
      if (!adminStatus) {
        const currentRole = userData.role || 'none';
        setError(`This account does not have admin privileges. Current role: ${currentRole}. Please ensure your account has the 'admin' role in Firestore.`);
        setLoading(false);
      } else {
        // Successfully authenticated as admin
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in');
      setLoading(false);
    }
  };

  const handleAdminSignIn = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!adminUid.trim()) {
      setError('Please enter a Firebase UID');
      return;
    }

    // Check if user is admin
    if (!user || !isAdmin) {
      setError('You must be logged in as an admin to use this feature');
      return;
    }

    setAdminSignInLoading(true);

    try {
      // Get custom token from backend
      const result = await api.createCustomToken(adminUid);
      
      if (result.error) {
        setError(result.error);
        setAdminSignInLoading(false);
        return;
      }

      // Sign in with custom token
      await signInWithCustomTokenAuth(result.data.customToken);
      
      // Redirect to dashboard
      navigate('/host/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to sign in as user');
      setAdminSignInLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50/30 via-white to-indigo-50/20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-purple-50/20 to-indigo-50/10 blur-3xl -top-20 -right-20" />
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-purple-50/15 to-pink-50/10 blur-3xl -bottom-20 -left-20" />
      </div>

      {/* Home Button */}
      <Link
        to="/"
        className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20 flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
          alt="Spirara"
          className="w-10 h-10 sm:w-12 sm:h-12"
        />
      </Link>

      <div className="relative z-10 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl sm:text-5xl font-light text-gray-900 tracking-tight">
              Admin Login
            </h1>
          </div>
          <p className="text-lg text-gray-600 font-light mb-4">
            Super Admin: Sign in as any user
          </p>
          <div className="mt-6 p-4 bg-purple-50/50 rounded-lg border border-purple-100">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="text-purple-800">Super Admin Feature</strong> – Sign in with your admin credentials, then enter any Firebase UID to impersonate that user.
            </p>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          {!isAdmin ? (
            // Admin Authentication Form
            <form className="space-y-6" onSubmit={handleAdminAuth}>
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Email address
                  </label>
                  <input
                    id="adminEmail"
                    name="adminEmail"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white/50"
                    placeholder="admin@example.com"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Password
                  </label>
                  <input
                    id="adminPassword"
                    name="adminPassword"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white/50"
                    placeholder="••••••••"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full group bg-purple-700 hover:bg-purple-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-purple-200/50 hover:shadow-xl hover:shadow-purple-300/50 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  'Signing in...'
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Sign in as Admin
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          ) : (
            // Admin Impersonation Form
            <form className="space-y-6" onSubmit={handleAdminSignIn}>
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                <p className="text-sm text-green-800 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>You are authenticated as an admin</span>
                </p>
              </div>

              <div>
                <label htmlFor="adminUid" className="block text-sm font-medium text-gray-700 mb-2">
                  Firebase User UID
                </label>
                <input
                  id="adminUid"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="Enter Firebase UID"
                  value={adminUid}
                  onChange={(e) => setAdminUid(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the Firebase UID of the user you want to sign in as
                </p>
              </div>

              <button
                type="submit"
                disabled={adminSignInLoading || !adminUid.trim()}
                className="w-full bg-purple-700 hover:bg-purple-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-purple-200/50 hover:shadow-xl hover:shadow-purple-300/50 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {adminSignInLoading ? (
                  'Signing in...'
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Sign in as User
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <div className="pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/host/dashboard')}
                  className="w-full text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

